import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Injector } from '@angular/core';
// import { ApiHttpClient, Deferred, NavigationService, OperationCanceledException } from '../../modules/tcm-core.module';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import { filter, map } from 'rxjs/operators';
import { SingletonCheckHelper } from '../../helpers/singleton-check.helper';
import { AuthenticationMethodResponse, AuthenticationResponse, LoginViewModel, LogoutResponse, RenewPasswordViewModel } from '../../models/logins';
import { AuthenticationStorageService } from './authentication-storage.service';
import { Deferred } from '../../modules/tcm-core.module/helpers/deferred';
import { ApiHttpClient } from '../../modules/tcm-core.module/services/api-http-client';
import { NavigationService } from '../../modules/tcm-core.module/services/navigation.service';
import { OperationCanceledException } from '../../modules/tcm-core.module';

interface ResponseWrapper<T> {
  isSuccess: boolean;
  data: T;
  error: { message: string }
}

@Injectable()
export class LoginApiService {
  private refreshCancellationTokenSource: Deferred;

  private singleSignOnCancellationTokenSource: Deferred;

  constructor(
    private readonly http: ApiHttpClient,
    private readonly storage: AuthenticationStorageService,
    private readonly injector: Injector,
    @Inject(DOCUMENT) private document: Document) {
    SingletonCheckHelper.ensureSingleton(this);
  }

  determineAuthenticationMethodAsync(email: string): Promise<AuthenticationMethodResponse> {
    const data = { email: email };

    return this.http.getJsonAsync<AuthenticationMethodResponse>('auth/determineauthenticationmethod', data, null, false);
  }

  async loginAsync(request: LoginViewModel): Promise<AuthenticationResult> {
    const response = await this.http.postJsonAsync<AuthenticationResponse>('auth/login', request, null, false);

    if (response.user) {
      this.storage.setCurrentUser(response.user);

      return AuthenticationResult.Success;
    }
    else if (response.passwordChangeRequired) {
      return AuthenticationResult.PasswordChangeRequired;
    }

    return AuthenticationResult.PasswordChangeRequired
  }

  async logoutAsync(fullLogout?: boolean): Promise<any> {
    try {
      if (fullLogout === true) {
        const response = await this.http.getJsonAsync<LogoutResponse>('auth/logout', null, null, false);

        if (response.externalLogOutUrl) {
          this.openNewTab_Click(response.externalLogOutUrl);
        }
      }
    }
    catch (e) {
      console.error(e);
    }
    finally {
      this.storage.setCurrentUser(null);
    }
  }

  renewPasswordAsync(model: RenewPasswordViewModel, cancellationToken: Promise<any>): Promise<any> {
    return this.http.postJsonAsync<LogoutResponse>('auth/renewpassword', model, cancellationToken, false);
  }

  async refreshTokenAsync() {
    if (this.refreshCancellationTokenSource) {
      this.refreshCancellationTokenSource.resolve();
    }

    if (!this.storage.getCurrentUser()) {
      return;
    }

    const user = this.storage.getCurrentUser();

    const tokenExpiresAfter = new Date(user.tokenExpiresAt);
    const currentDateTime = new Date();
    // check token is not outdated. If true, do nothing
    if (currentDateTime > tokenExpiresAfter) {
      await this.logoutAsync();
      return;
    }

    this.refreshCancellationTokenSource = new Deferred();

    try {
      const response = await this.http.postJsonAsync<AuthenticationResponse>('auth/RefreshToken', null, this.refreshCancellationTokenSource.promise(), false);

      this.storage.setCurrentUser(response.user);
    }
    catch (ex) {
      console.error(`failed to refresh user authentication token: ${ex}`);
    }
  }

  async impersonateUserAsync(userId: number, cancellationToken: Promise<any>) {
    await this.refreshTokenAsync();

    if (!this.storage.getCurrentUser()) {
      return;
    }

    const response = await this.http.getJsonAsync<AuthenticationResponse>(`auth/Impersonation?id=${userId}`, null, cancellationToken);

    const navSvc = this.injector.get(NavigationService);

    await navSvc.navigate("/plug", null, { skipLocationChange: true });

    this.storage.setCurrentUser(response.user);

    await navSvc.navigate(null);
  }

  async stopImpersonationAsync(cancellationToken?: Promise<any>) {
    await this.refreshTokenAsync();

    if (!this.storage.getCurrentUser()) {
      return;
    }

    const response = await this.http.postJsonAsync<AuthenticationResponse>(`auth/Impersonation`, null, cancellationToken);

    const navSvc = this.injector.get(NavigationService);

    await navSvc.navigate("/plug", null, { skipLocationChange: true });

    this.storage.setCurrentUser(response.user);

    await navSvc.navigate('/admin/users');
  }

  makeSingleSignOnAsync(email: string, cancellationToken: Promise<any>): Promise<any> {
    return new Promise((resolve, reject) => {
      try {
        if (this.singleSignOnCancellationTokenSource) {
          this.singleSignOnCancellationTokenSource.resolve();
        }
        this.singleSignOnCancellationTokenSource = new Deferred();

        const innerCancellationToken = Promise.race([cancellationToken, this.singleSignOnCancellationTokenSource.promise()]);

        const url = `api/auth/SingleSignOnStart?email=${encodeURIComponent(email)}`;

        this.openNewTab_Post(url);

        const origin = this.document.location.origin;

        const observer = fromEvent<MessageEvent>(window, 'message')
          .pipe(
            filter(x => x.origin === origin && typeof x.data === 'string'),
            map(x => JSON.parse(x.data) as ResponseWrapper<AuthenticationResponse>),
            map(response => {
              if (response.isSuccess) {
                return response.data;
              }

              throw response.error.message;
            })
          );

        let ssoSubscription;

        ssoSubscription = observer.subscribe(
          response => {
            this.storage.setCurrentUser(response.user);
            resolve(null);
          },
          (e: string) => {
            reject(e);
          },
          () => {
            ssoSubscription.unsubscribe();
          }
        );

        innerCancellationToken.then(x => {
          reject(new OperationCanceledException());
          ssoSubscription.unsubscribe();
        });
      }
      catch (e) {
        console.error(e);
        reject('Unknown error.');
      }
    })
  }

  private openNewTab_Post(url: string) {
    const formElement = this.document.createElement('form');
    formElement.action = url;
    formElement.method = 'POST';
    formElement.rel = 'opener';
    formElement.target = '_blank';
    formElement.style.display = 'none';

    try {
      this.document.body.appendChild(formElement);
      formElement.submit();
    }
    finally {
      this.document.body.removeChild(formElement);
    }
  }

  private openNewTab_Click(url: string) {
    const linkElement = this.document.createElement('a');

    linkElement.style.display = 'none';
    linkElement.rel = 'opener';
    linkElement.target = '_blank';
    linkElement.href = url;

    this.document.body.appendChild(linkElement);

    linkElement.click();

    this.document.body.removeChild(linkElement);
  }
}

export enum AuthenticationResult {
  Success = 0,
  PasswordChangeRequired = 1
}
